import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Brand } from "@/components/Logo";
import { ShieldCheck, Check, Loader2, PenLine, BadgeCheck, FileText } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PKG_COLORS = { cancellazione_999: "999€", cancellazione_1799: "1.799€", cancellazione_2499: "2.499€" };

export default function OfferPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [state, setState] = useState({ loading: true, error: null, offer: null });
  const [nome, setNome] = useState("");
  const [accetto, setAccetto] = useState(false);
  const [accettoClausole, setAccettoClausole] = useState(false);
  const [firmaImg, setFirmaImg] = useState(null);
  const [sending, setSending] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    fetch(`${API}/offerte/${orderId}?t=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Offerta non trovata");
        return r.json();
      })
      .then((offer) => setState({ loading: false, error: null, offer }))
      .catch((e) => setState({ loading: false, error: e.message, offer: null }));
  }, [orderId, token]);

  const firma = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await fetch(`${API}/offerte/${orderId}/firma`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ t: token, nome_firma: nome, accetto, accetto_clausole: accettoClausole, firma_img: firmaImg }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Errore durante la firma");
      setSigned(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  const o = state.offer;

  return (
    <div className="min-h-screen bg-[#F6FAFD]" data-testid="offer-page">
      <header className="bg-white border-b border-[#E2E8F0] py-5 px-5">
        <div className="max-w-3xl mx-auto"><Brand /></div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12">
        {state.loading && (
          <div className="flex justify-center py-20" data-testid="offer-loading"><Loader2 className="animate-spin text-[#00AEEF]" size={32} /></div>
        )}

        {state.error && (
          <div className="card rounded-3xl p-10 text-center" data-testid="offer-error">
            <h1 className="text-xl font-bold text-[#003087]">Offerta non trovata</h1>
            <p className="mt-3 text-sm text-[#64748B]">{state.error}. Se hai ricevuto il link via email, verifica di averlo copiato per intero o contattaci su WhatsApp.</p>
          </div>
        )}

        {o && (signed || o.firmato) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card rounded-3xl p-10 text-center" data-testid="offer-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#E6F6FD] flex items-center justify-center"><BadgeCheck size={32} className="text-[#00AEEF]" /></div>
            <h1 className="text-2xl font-extrabold text-[#003087] mt-6">Contratto firmato. Si parte.</h1>
            <p className="mt-4 text-sm text-[#1A1A1A] leading-relaxed max-w-md mx-auto">
              I nostri esperti richiedono ora alla banca o alla finanziaria segnalante tutta la documentazione della tua segnalazione:
              è l'esame di quei documenti che ci permette di studiare la strategia migliore e presentare la richiesta di cancellazione.
              La banca ha 90 giorni per rispondere, ma nella maggior parte dei casi risponde molto prima. Ti aggiorniamo a ogni passaggio.
            </p>
            <p className="mt-5 text-xs font-bold text-[#00AEEF] uppercase tracking-wider">Ricorda: paghi solo a cancellazione avvenuta</p>
          </motion.div>
        )}

        {o && !signed && !o.firmato && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00AEEF]">Offerta riservata</p>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-[#003087] mt-3" data-testid="offer-title">{o.name}, le tue visure sono pronte.</h1>
              <p className="mt-3 text-sm text-[#64748B]">Abbiamo individuato <strong className="text-[#003087]">{o.segnalazioni} segnalazioni negative</strong> a tuo nome. Ecco il pacchetto studiato per te.</p>
            </div>

            <div className="rounded-3xl bg-[#003087] text-white p-8 shadow-2xl relative overflow-hidden" data-testid="offer-package-card">
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#00AEEF]/20 blur-[80px] pointer-events-none" />
              <div className="flex items-center justify-between flex-wrap gap-3">
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] bg-white/10 rounded-full px-4 py-1.5">Pacchetto · {o.label}</span>
                <span className="inline-flex items-center gap-1.5 bg-[#00AEEF] text-[11px] font-bold uppercase tracking-wider rounded-full px-3 py-1.5"><ShieldCheck size={13} /> 0€ anticipo</span>
              </div>
              <div className="mt-6 flex items-end gap-3">
                <span className="text-6xl font-extrabold tracking-tight" data-testid="offer-price">{PKG_COLORS[o.pacchetto] || `${o.price}€`}</span>
                <span className="text-[#9BDDF7] text-xs font-bold mb-2.5 uppercase tracking-wide">solo a cancellazione effettuata</span>
              </div>
              <ul className="mt-6 space-y-2.5">
                {[
                  "Paghi solo ed esclusivamente a cancellazione consegnata — mai prima",
                  "Rateizzabile in 3 o 6 rate senza interessi dopo il risultato",
                  "Garanzia scritta: nessuna cancellazione, nessun pagamento",
                  "I nostri esperti richiedono i documenti alla banca (risposta entro 90 giorni, spesso molto meno)",
                ].map((f, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-[#DCE7F5]"><Check size={16} className="text-[#00AEEF] shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
            </div>

            <div className="card rounded-3xl p-7" data-testid="offer-contract">
              <h2 className="text-lg font-bold text-[#003087] flex items-center gap-2 mb-1"><FileText size={18} className="text-[#00AEEF]" /> Il tuo Contratto di Mandato</h2>
              <p className="text-xs text-[#64748B] mb-4">Compilato automaticamente con i tuoi dati, il numero di segnalazioni e l'importo del pacchetto. Leggilo con calma prima di firmare.</p>
              <div className="max-h-80 overflow-y-auto rounded-2xl bg-[#F6FAFD] border border-[#E2E8F0] p-5 space-y-4" data-testid="offer-contract-text">
                {(o.contratto || []).map((s, i) => (
                  <div key={i}>
                    {s.titolo && <p className="text-xs font-bold text-[#003087]">{s.titolo}</p>}
                    {s.testo && <p className="text-xs text-[#1A1A1A] leading-relaxed mt-1 whitespace-pre-line">{s.testo}</p>}
                  </div>
                ))}
                <p className="text-xs text-[#1A1A1A] leading-relaxed border-t border-[#E2E8F0] pt-3">
                  Luogo e Data: {new Date().toLocaleDateString("it-IT")} · Il Cliente: {o.name} · Sdebitiamoci Srl (il legale rappresentante)
                </p>
                <p className="text-xs text-[#64748B] leading-relaxed">
                  Per espressa visione ed accettazione delle condizioni di cui ai punti: 2) oggetto del contratto; 3) obblighi del Cliente; 6) recesso; 7) foro competente.
                </p>
              </div>
            </div>

            <form onSubmit={firma} className="card rounded-3xl p-8" data-testid="offer-sign-form">
              <h2 className="text-lg font-bold text-[#003087] flex items-center gap-2"><PenLine size={18} className="text-[#00AEEF]" /> Firma il contratto</h2>
              <p className="mt-2 text-xs text-[#64748B] leading-relaxed">Firmando accetti l'incarico per il pacchetto «{o.label}» al prezzo indicato, da corrispondere solo a cancellazione avvenuta. La firma è registrata con data, ora e indirizzo IP.</p>
              <input data-testid="offer-sign-name" type="text" required minLength={3} placeholder="Nome e cognome (firma digitale)" value={nome}
                onChange={(e) => setNome(e.target.value)} className="input-field mt-5" />
              <label className="mt-4 flex items-start gap-2.5 text-xs text-[#1A1A1A] cursor-pointer">
                <input data-testid="offer-sign-accept" type="checkbox" checked={accetto} onChange={(e) => setAccetto(e.target.checked)} className="mt-0.5 accent-[#00AEEF]" />
                <span>Ho letto e accetto le condizioni: nessun anticipo, pagamento del pacchetto solo a cancellazione confermata, trattamento dei dati secondo GDPR.</span>
              </label>
              <label className="mt-3 flex items-start gap-2.5 text-xs text-[#1A1A1A] cursor-pointer">
                <input data-testid="offer-sign-clausole" type="checkbox" checked={accettoClausole} onChange={(e) => setAccettoClausole(e.target.checked)} className="mt-0.5 accent-[#00AEEF]" />
                <span><strong>Accetto espressamente</strong> le clausole 2 (oggetto), 3 (obblighi del Cliente), 6 (recesso) e 7 (foro competente), ai sensi degli artt. 1341-1342 c.c.</span>
              </label>
              <div className="mt-5">
                <label className="block text-xs font-bold text-[#003087] mb-1.5">La tua firma (con il dito o il mouse) *</label>
                <SignaturePad onChange={setFirmaImg} testid="offer-signature" />
              </div>
              <button data-testid="offer-sign-submit" disabled={sending || !accetto || !accettoClausole || !firmaImg || nome.trim().length < 3}
                className="btn-green mt-6 w-full rounded-full py-4 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                {sending && <Loader2 size={15} className="animate-spin" />} Firma e accetta il contratto
              </button>
            </form>
          </motion.div>
        )}
      </main>
    </div>
  );
}
